import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { baseApiService } from "../../services/api/axios-config/axios-config";
import { InputFileUpload } from "../formulario/input-file/inputFile";

interface ModalImportacaoNotaProps {
  open: boolean;
  onClose: () => void;
  onImportSuccess?: (data: any) => void;
  onImportError?: (error: any) => void;
  title?: string;
  acceptedFormats?: string;
  importEndpoint?: string;
  helperText?: string;
}

interface ImportFormData {
  importFile: string | null;
}

export function ModalImportacaoNota({
  open,
  onClose,
  onImportSuccess,
  onImportError,
  title = "Importar Nota Fiscal",
  acceptedFormats = ".pdf,.xml,.txt",
  importEndpoint = "/entrada-notas-compra/importar",
  helperText = "Selecione um arquivo para importar os dados da nota fiscal",
}: ModalImportacaoNotaProps) {
  const [isImporting, setIsImporting] = useState(false);

  const methods = useForm<ImportFormData>({
    defaultValues: {
      importFile: null,
    },
  });

  const { watch, reset } = methods;
  const importFile = watch("importFile");

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Selecione um arquivo para importar!");
      return;
    }

    setIsImporting(true);

    try {
      // Chama a API de importação com o caminho do arquivo
      const importUrl = `${importEndpoint}/${importFile}`;
      const importResponse = await baseApiService.post(importUrl);

      toast.success("Nota fiscal importada com sucesso, Confira se todos os dados estão corretos!");

      // Callback de sucesso
      if (onImportSuccess) {
        onImportSuccess(importResponse.data);
      }

      // Fechar modal após sucesso
      handleClose();

    } catch (error: any) {
      console.error("Erro na importação:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Erro ao importar nota fiscal";

      toast.error(errorMessage);

      // Callback de erro
      if (onImportError) {
        onImportError(error);
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      reset();
      onClose();
    }
  };

  // Função auxiliar para extrair o nome do arquivo
  const getFileName = (filePath: string | null): string => {
    if (!filePath) return "";
    return filePath.split("/").pop() || "";
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {title}
        <IconButton
          onClick={handleClose}
          disabled={isImporting}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {helperText}
          </Typography>

          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              backgroundColor: "#fafafa",
            }}
          >
            <InputFileUpload
              name="importFile"
              label={`Selecionar Arquivo (${acceptedFormats.replace(/\./g, "").toUpperCase()})`}
              methods={methods}
              helperText={`Formatos aceitos: ${acceptedFormats.replace(/\./g, "").toUpperCase()}`}
              acceptedFormats={acceptedFormats}
              required={true}
            />
          </Box>

          {importFile && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#e8f5e8", borderRadius: 1 }}>
              <Typography variant="body2" color="success.main">
                ✓ Arquivo carregado: {getFileName(importFile)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Clique em "Importar Dados" para processar o arquivo
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={isImporting}
          variant="outlined"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleImport}
          disabled={!importFile || isImporting}
          variant="contained"
          color="primary"
        >
          {isImporting ? "Importando..." : "Importar Dados"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}